import { useDarkMode } from "@/hooks/useDarkMode";
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_STRING as api } from '@/App';
import { toast } from 'sonner';

import { logger } from '@/utils/logger';

import { ArrowLeft, ArrowRight, Heart, Plus, RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ConfirmDialog from '@/components/ConfirmDialog';
const OCCASIONS = [
  'Birthday', 'Anniversary', 'Wedding', 'Graduation', 'Holiday',
  'Just Because', 'Thank You', 'Celebration', 'Other'
];

// Single-screen questionnaire creation. Step 1 = pick occasion. Step 2 =
// review/edit the generated questions inline. One final click saves and
// jumps to the share screen. (Previously this was three separate pages.)
export default function KeepsakeCreate({ user }) {
  const darkMode = useDarkMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nameParam = searchParams.get('name') || '';

  const [recipient, setRecipient] = useState(null);
  const [recipientLoading, setRecipientLoading] = useState(true);
  const [manualName, setManualName] = useState('');
  const [manualRelationship, setManualRelationship] = useState('');
  const [occasion, setOccasion] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);

  // Step 2 state, populated after generation
  const [questionnaireId, setQuestionnaireId] = useState(null);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${api}/recipients`, { credentials: 'include' });
        if (!res.ok) throw new Error('failed');
        const list = await res.json();
        if (cancelled) return;
        const match = nameParam
          ? list.find((r) => r.name.toLowerCase() === nameParam.toLowerCase())
          : null;
        setRecipient(match || null);
        if (nameParam && !match) setManualName(nameParam);
      } catch (err) {
        logger.error('Failed to fetch recipients:', err);
        toast.error('Failed to load recipient profile');
      } finally {
        if (!cancelled) setRecipientLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [nameParam]);

  const effectiveName = recipient?.name || manualName.trim();
  const effectiveRelationship = recipient?.relationship || manualRelationship;

  const handleGenerate = async (e) => {
    e?.preventDefault?.();
    if (!effectiveName || !occasion) return;
    setGenerating(true);
    try {
      const res = await fetch(`${api}/keepsakes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recipient_name: effectiveName,
          occasion,
          relationship: effectiveRelationship || ''
        })
      });
      if (!res.ok) throw new Error('Generate failed');
      const data = await res.json();
      setQuestionnaireId(data.id);
      setQuestions(data.questions || []);
      toast.success('Questions ready! Edit anything that needs a tweak.');
    } catch (err) {
      logger.error(err);
      toast.error('Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  // Allow regenerating questions if the user doesn't love the first set.
  const handleRegenerate = async () => {
    if (!questionnaireId) return;
    setGenerating(true);
    try {
      // Delete the old questionnaire so a new one is generated with the
      // same recipient + occasion combination.
      await fetch(`${api}/keepsakes/${questionnaireId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setQuestionnaireId(null);
      setQuestions([]);
      await handleGenerate();
    } catch (err) {
      logger.error(err);
      toast.error('Failed to regenerate');
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!questionnaireId) return;
    const cleaned = questions.map((q) => q.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      toast.error('Add at least one question first.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${api}/keepsakes/${questionnaireId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions: cleaned })
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success('Saved! Time to share it.');
      navigate(`/keepsakes/${questionnaireId}/questionnaire`);
    } catch (err) {
      logger.error(err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (idx, value) =>
    setQuestions((prev) => prev.map((q, i) => (i === idx ? value : q)));
  const removeQuestion = (idx) =>
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  const addQuestion = () => setQuestions((prev) => [...prev, '']);

  const step = questionnaireId ? 2 : 1;

  return (
    <div className="min-h-screen page-enter" style={{ background: darkMode.bgPrimary }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back link, returns to the recipient's profile if we know who they
            are, otherwise to the Keepsakes index. */}
        <button
          onClick={() => navigate(recipient ? `/keepsakes?recipientName=${encodeURIComponent(recipient.name)}` : '/keepsakes')}
          className="flex items-center gap-2 mb-6 text-sm hover:underline"
          style={{ color: '#9E6752' }}
          data-testid="kc-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {recipient ? `Back to ${recipient.name}` : 'Back to Keepsakes'}
        </button>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6">
          <StepDot active={step === 1} done={step > 1} label="1" />
          <div className="flex-1 h-px" style={{ background: step > 1 ? '#9E6752' : darkMode.isDark ? 'rgba(255,255,255,0.15)' : '#E5E5E5' }} />
          <StepDot active={step === 2} done={false} label="2" />
        </div>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B', fontFamily: 'Playfair Display, serif' }}>
            {step === 1 ? 'Create a questionnaire' : `Review ${effectiveName || 'your'} questions`}
          </h1>
          <p className="text-base" style={{ color: darkMode.isDark ? '#E5E5E5' : '#73766A' }}>
            {step === 1
              ? "Pick the occasion. We'll handle the rest."
              : "These were drafted from their profile. Tweak anything that doesn't sound quite right, then save to get a share link."}
          </p>
        </div>

        {/* Recipient summary, always visible so you know who this is for */}
        {recipientLoading ? (
          <div className="h-16 rounded-lg animate-pulse mb-6" style={{ background: darkMode.isDark ? 'rgba(255,255,255,0.05)' : '#F3F4F6' }} />
        ) : recipient ? (
          <div
            className="flex items-center gap-3 p-4 rounded-lg mb-6"
            style={{
              background: `${recipient.color || '#9E6752'}20`,
              border: `1px solid ${recipient.color || '#9E6752'}40`
            }}
            data-testid="kc-recipient-card"
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.9)' }}>
              <Heart className="w-5 h-5" style={{ color: recipient.color || '#9E6752' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold" style={{ color: darkMode.isDark ? '#FFFAF5' : '#20212B' }}>
                {recipient.name}
              </p>
              <p className="text-xs opacity-70" style={{ color: darkMode.isDark ? '#E5E5E5' : '#73766A' }}>
                {recipient.relationship || 'Recipient'}{occasion ? ` • ${occasion}` : ''}
              </p>
            </div>
          </div>
        ) : (
          <div className="p-4 rounded-lg mb-6" style={{ background: darkMode.isDark ? 'rgba(254,215,165,0.1)' : '#FFF5E6', border: `1px solid ${darkMode.isDark ? 'rgba(254,215,165,0.3)' : '#FED7A5'}` }}>
            <p className="text-sm" style={{ color: darkMode.isDark ? '#FED7A5' : '#534145' }}>
              Tell us who this questionnaire is for. After they answer, you can save it to a keepsake from the Browse page.
            </p>
          </div>
        )}

        {/* STEP 1, Occasion picker */}
        {step === 1 && (
          <form
            onSubmit={handleGenerate}
            className="rounded-xl p-6 sm:p-8 space-y-6"
            style={{
              background: darkMode.isDark ? '#2D4354' : 'white',
              boxShadow: darkMode.isDark ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.08)'
            }}
          >
            {!recipient && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>
                    Who is this for? *
                  </label>
                  <input
                    type="text"
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                    placeholder="e.g. Mom, Alex, Grandpa Joe"
                    maxLength={60}
                    required
                    data-testid="kc-name-input"
                    className="w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9E6752]"
                    style={{
                      background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                      color: darkMode.isDark ? '#FED7A5' : '#20212B',
                      border: `1px solid ${darkMode.isDark ? '#FED7A5' : '#D1D5DB'}`
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>
                    Your relationship
                  </label>
                  <select
                    value={manualRelationship}
                    onChange={(e) => setManualRelationship(e.target.value)}
                    data-testid="kc-relationship-select"
                    className="w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9E6752]"
                    style={{
                      background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                      color: darkMode.isDark ? '#FED7A5' : '#20212B',
                      border: `1px solid ${darkMode.isDark ? '#FED7A5' : '#D1D5DB'}`
                    }}
                  >
                    <option value="" style={{ background: darkMode.isDark ? '#2D4354' : 'white' }}>Select (optional)</option>
                    {['Partner', 'Friend', 'Family', 'Parent', 'Child', 'Sibling', 'Colleague'].map((rel) => (
                      <option key={rel} value={rel} style={{ background: darkMode.isDark ? '#2D4354' : 'white' }}>{rel}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: darkMode.isDark ? '#FED7A5' : '#20212B' }}>
                What's the occasion? *
              </label>
              <select
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                className="w-full p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#9E6752]"
                style={{
                  background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                  color: darkMode.isDark ? '#FED7A5' : '#20212B',
                  border: `1px solid ${darkMode.isDark ? '#FED7A5' : '#D1D5DB'}`
                }}
                required
                data-testid="kc-occasion-select"
              >
                <option value="" style={{ background: darkMode.isDark ? '#2D4354' : 'white' }}>Select occasion</option>
                {OCCASIONS.map((occ) => (
                  <option key={occ} value={occ} style={{ background: darkMode.isDark ? '#2D4354' : 'white' }}>{occ}</option>
                ))}
              </select>
            </div>

            <div className="p-4 rounded-lg flex gap-3" style={{ background: darkMode.isDark ? 'rgba(254, 215, 165, 0.15)' : '#FEF3C7', border: `1px solid ${darkMode.isDark ? 'rgba(254, 215, 165, 0.3)' : 'transparent'}` }}>
              <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: darkMode.isDark ? '#FED7A5' : '#92400E' }} />
              <p className="text-sm leading-relaxed" style={{ color: darkMode.isDark ? '#FED7A5' : '#92400E' }}>
                We'll put together about 10 questions tailored to {effectiveName || 'this person'}'s interests, notes, and your relationship. You can edit, add, or remove any of them right here before sharing.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/keepsakes')}
                className="flex-1"
                style={{
                  background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                  color: darkMode.isDark ? '#FED7A5' : '#20212B',
                  border: `1px solid ${darkMode.isDark ? '#FED7A5' : '#D1D5DB'}`
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={generating || !effectiveName || !occasion}
                className="flex-1"
                style={{ background: '#9E6752', color: 'white' }}
                data-testid="kc-submit-btn"
              >
                {generating ? 'Generating questions…' : 'Generate Questions'}
              </Button>
            </div>
          </form>
        )}

        {/* STEP 2, Review / edit questions inline */}
        {step === 2 && (
          <>
            <div className="space-y-3 mb-4">
              {questions.map((q, idx) => (
                <div
                  key={idx}
                  className="rounded-xl p-4"
                  style={{
                    background: darkMode.isDark ? '#2D4354' : 'white',
                    boxShadow: darkMode.isDark ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.05)'
                  }}
                  data-testid={`kc-q-row-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: '#9E6752', color: 'white' }}>
                      {idx + 1}
                    </span>
                    <Textarea
                      value={q}
                      onChange={(e) => updateQuestion(idx, e.target.value)}
                      rows={2}
                      className="flex-1 resize-y min-h-[60px]"
                      ref={(el) => {
                        if (el) {
                          el.style.height = 'auto';
                          el.style.height = `${el.scrollHeight}px`;
                        }
                      }}
                      style={{
                        background: darkMode.isDark ? 'rgba(255,255,255,0.06)' : '#FAFAFA',
                        color: darkMode.isDark ? '#FFFAF5' : '#20212B',
                        border: `1px solid ${darkMode.isDark ? 'rgba(254, 215, 165, 0.3)' : '#E5E5E5'}`,
                        overflow: 'hidden'
                      }}
                      data-testid={`kc-q-input-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeQuestion(idx)}
                      className="p-2 rounded-lg transition-colors hover:bg-red-500/10 flex-shrink-0"
                      style={{ color: '#EF4444' }}
                      title="Remove this question"
                      data-testid={`kc-q-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <Button
              type="button"
              onClick={addQuestion}
              variant="outline"
              className="w-full mb-3"
              style={{
                borderStyle: 'dashed',
                borderColor: darkMode.isDark ? 'rgba(254, 215, 165, 0.4)' : '#D1D5DB',
                color: darkMode.isDark ? '#FED7A5' : '#534145',
                background: 'transparent'
              }}
              data-testid="kc-add-q-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add another question
            </Button>

            <Button
              type="button"
              onClick={() => setShowRegenConfirm(true)}
              variant="ghost"
              disabled={generating}
              className="w-full mb-6"
              style={{ color: darkMode.isDark ? '#FED7A5' : '#9E6752' }}
              data-testid="kc-regenerate-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Regenerating…' : 'Regenerate all questions'}
            </Button>

            <ConfirmDialog
              open={showRegenConfirm}
              onOpenChange={setShowRegenConfirm}
              onConfirm={handleRegenerate}
              title="Regenerate all questions?"
              description="This replaces the current questions with a fresh set. Any edits you've made will be lost."
              confirmText="Regenerate"
              variant="default"
            />

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/keepsakes')}
                className="flex-1"
                style={{
                  background: darkMode.isDark ? 'rgba(255, 255, 255, 0.1)' : 'white',
                  color: darkMode.isDark ? '#FED7A5' : '#20212B',
                  border: `1px solid ${darkMode.isDark ? '#FED7A5' : '#D1D5DB'}`
                }}
              >
                Save for later
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1"
                style={{ background: '#9E6752', color: 'white' }}
                data-testid="kc-save-btn"
              >
                {saving ? 'Saving…' : (
                  <>
                    Save & Get Share Link
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StepDot({ active, done, label }) {
  const bg = done ? '#9E6752' : active ? '#9E6752' : '#E5E5E5';
  const color = done || active ? 'white' : '#73766A';
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all"
      style={{ background: bg, color, opacity: active || done ? 1 : 0.6 }}
    >
      {label}
    </div>
  );
}
